/**
 * @fileoverview 各種バリデーション
 */
import { isAxiosError } from 'axios'
import { LEAD_GEN } from '@/_core/endpoint'
import apiRequestManager from '@/_core/apiRequestManager'
import { isHiraganaOnly, normalizeKana } from '@/utilities/formatter'

/**
 * 有効なメールアドレスかどうか
 *
 * @param value 対象の文字列
 */
export function isValidEmail(value: string): boolean {
  const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return regex.test(value)
}

/**
 * 許可されたメールドメインかどうか
 *
 * @param invalidDomains 無効なメールドメインの配列
 * @param value 対象の文字列
 */
export function isValidMailDomain(invalidDomains: string[], value: string): boolean {
  const invalidDomainRegexp = new RegExp('@([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\\.+|)(' + invalidDomains.join('|') + ')$', 'i')
  return !invalidDomainRegexp.test(value)
}

/**
 * 有効な URL かどうか
 *
 * @param value 対象の文字列
 */
export function isValidURL(value: string): boolean {
  try {
    new URL(value)
    return true
  } catch {
    return false
  }
}

/**
 * 有効なパスワードかどうか
 *
 * @param value 対象の文字列
 */
export function isValidPassword(value: string): boolean {
  const regex = /^[a-zA-Z0-9!-/:-@¥[-`{-~]{8,64}$/
  return regex.test(value)
}

/**
 * 半角数字かどうか
 *
 * @param value 対象の文字列
 */
export function isValidNumber(value: string | number): boolean {
  const _value = value.toString()
  const regex = /^[0-9]+$/
  return regex.test(_value)
}

/**
 * かな（ひらがな・カタカナ）のみかどうか
 *
 * @description 表記ゆれ（半角カタカナ・結合文字の濁点・姓名の間の空白）は正規化してから判定するため、数字・英字・記号・漢字が混じる場合だけ false になる
 * @param value 対象の文字列
 */
export function isValidKana(value: string): boolean {
  return isHiraganaOnly(normalizeKana(value))
}

/**
 * 有効な日付かどうか
 *
 * @param value 対象の文字列
 */
export function isValidDate(value: string): boolean {
  if (value === '') return false
  if (value !== value.trim()) return false

  // 日付（必須）と時刻（任意）を許容するフォーマット。
  //   - 日付部:   YYYY-MM-DD または YYYY/MM/DD（区切りは `\2` で前後一致を要求し YYYY-MM/DD のような混在を弾く）
  //   - 区切り:   日付と時刻の間は `T` または半角スペース
  //   - 時刻部:   HH:MM、HH:MM:SS、HH:MM:SS.fff（小数点以下は1〜9桁まで）
  //   - タイムゾーン: 末尾に `Z` または `±HH:MM` / `±HHMM`
  // 例: 2024-01-01 / 2024/01/01 / 2024-01-01T10:00 / 2024-01-01T10:00:00.123+09:00
  const matched = value.match(
    /^(\d{4})([-/])(\d{1,2})\2(\d{1,2})(?:[T ](\d{1,2}):(\d{1,2})(?::(\d{1,2})(?:\.\d{1,9})?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/
  )
  if (!matched) return false

  const year = Number(matched[1])
  const month = Number(matched[3])
  const day = Number(matched[4])

  if (month < 1 || month > 12) return false

  // 2024-02-31 のような存在しない日付を弾く
  const maxDay = new Date(year, month, 0).getDate()
  if (day < 1 || day > maxDay) return false

  // 時刻が含まれている場合は時・分・秒の範囲をチェック
  if (matched[5] !== undefined) {
    const hour = Number(matched[5])
    const minute = Number(matched[6])
    const second = matched[7] !== undefined ? Number(matched[7]) : 0

    if (hour > 23) return false
    if (minute > 59) return false
    if (second > 59) return false
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return false

  return true
}

/**
 * 電話番号の表記ゆれを揃える（前後空白の除去、空白・ハイフンの除去、全角数字の半角化）
 *
 * @param value 対象の文字列
 */
export function normalizePhoneNumber(value: string | number): string {
  return value
    .toString()
    .trim()
    .replace(/[\s-]+/g, '')
    .replace(/[０-９]/g, (m) => String.fromCharCode(m.charCodeAt(0) - 0xfee0))
}

/**
 * 電話番号として正しいフォーマットかどうか
 *
 * @param value 対象の文字列
 */
export function isValidPhoneNumberFormat(value: string | number): boolean {
  return /^[0-9]{10,15}$/.test(normalizePhoneNumber(value))
}

/**
 * 電話番号の照会結果
 * - valid: 実在する番号として確認できた
 * - invalid: 実在しない番号として弾かれた
 * - unverifiable: 照会 API に到達できず確認できなかった（通信断・サーバ障害など）
 */
export type PhoneNumberVerification = 'valid' | 'invalid' | 'unverifiable'

/**
 * 電話番号の実在性を照会する（Twilio Lookup）
 *
 * @description 照会 API は実在しない番号を 404 で返す。それ以外の失敗（通信断・5xx・認証系の 4xx など）は「確認できなかった」として区別し、判定は呼び出し側に委ねる。
 *              エンドポイント側で hasDefaultErrorHandling を false にしているため、失敗は画面のエラー状態を書き換えずに例外として届く。
 * @param value 対象の文字列
 */
export async function verifyPhoneNumber(value: string | number): Promise<PhoneNumberVerification> {
  try {
    await apiRequestManager.handleRequest({
      endpoint: LEAD_GEN.VALID_PHONE_NUMBER,
      params: { phone_number: normalizePhoneNumber(value) },
    })
    return 'valid'
  } catch (error) {
    // 実在しない番号だけが 404。それ以外（通信断・5xx・認証系の 4xx など）は照会できなかったものとして扱う
    return isAxiosError(error) && error.response?.status === 404 ? 'invalid' : 'unverifiable'
  }
}

/**
 * 入力されてるかどうか
 *
 * @param value 対象の文字列
 */
export function isInputed(value: any): boolean {
  if (value === undefined || value === null) return false

  if (Array.isArray(value)) {
    return !!value.length
  } else {
    switch (typeof value) {
      case 'string':
      case 'number':
        return value !== '' && value !== undefined && value !== null

      case 'boolean':
        return value === true

      default:
        // input type="file" の場合
        if (isFile(value)) return isValidFile(value)

        // 予期しない型が来た場合は false
        return false
    }
  }
}

/**
 * File オブジェクトかどうか
 *
 * @param value 判定する値
 */
export function isFile(value: any): boolean {
  return !!('File' in window && value instanceof File)
}

/**
 * Blob オブジェクトかどうか
 *
 * @param value 判定する値
 */
export function isBlob(value: any): boolean {
  return !!('Blob' in window && value instanceof Blob)
}

export function isValidFile(value: File): boolean {
  // ファイル取得不可
  if (!value) return false

  // ファイル容量なし
  if (value.size === 0) return false

  if (!value.type || value.type === 'error') return false

  return true
}

/**
 * 数値、または数値変換可能な文字列かどうか
 *
 * @param value 対象の値
 */
export function isNumber(value: any): boolean {
  return !Number.isNaN(parseInt(value))
}

/**
 * アルファベットが含まれているかどうか
 *
 * @param value 対象の文字列
 */
export function hasAlphabet(value: string): boolean {
  return /[a-zA-Z]/.test(value)
}

/**
 * 数字が含まれているかどうか
 *
 * @param value 対象の文字列
 */
export function hasNumber(value: string): boolean {
  return /[0-9]/.test(value)
}

/**
 * 記号が含まれているかどうか
 *
 * @param value 対象の文字列
 */
export function hasSymbol(value: string): boolean {
  return /[!-/:-@¥[-`{-~]/.test(value)
}

/**
 * 指定の桁数以上の値であるかどうか
 *
 * @param value 対象の文字列
 * @param min 最小桁数
 */
export function isValidMinLength(value: string, min: number): boolean {
  return value.length >= min
}
